import { useCallback, useEffect, useRef, useState } from 'react'
import { useTerminology } from '@/lib/useTerminology'
import { Eye, EyeOff, Trash2, Upload } from 'lucide-react'
import { erfuelltRecht, type ProjectEntityType } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { formCard } from '@/lib/form-styles'
import { useAuthStore } from '@/lib/auth.store'
import { deleteFile, fetchFiles, updateFile, uploadFile } from '../api/projects-phase-d.api'
import { oeffneProjektdatei } from '../api/project-tech.api'
import { ENTITY_TYPE_LABELS, FILE_KIND_LABELS, alsGroesse, alsZeitpunkt } from '../project-labels'
import type { ProjectFileRow } from '../types/phase-d.types'

/**
 * AP-35 Detailblatt 6 4.4 — **Dateien.**
 *
 * Dieselbe Fläche am Projekt, an der Position und am Arbeitsschritt; `entityType`
 * entscheidet, wohin eine neue Datei gehört und was die Liste zeigt.
 *
 * Das **Auge** ist kein Zierrat: Es schaltet `visibleInPwa`, und nur damit
 * erscheint eine Datei in der Auftragsmappe auf der Baustelle. Ohne das
 * Kennzeichen antwortet die PWA mit `404` — sie erfährt nicht einmal, dass es
 * die Datei gibt (Blatt 6 6.).
 */
export function ProjectFilesPanel({
  projectId,
  entityType,
  entityId,
  onChanged,
}: {
  projectId: string
  entityType: ProjectEntityType
  entityId: string
  onChanged?: () => Promise<void> | void
}) {
  const terms = useTerminology()
  const { user } = useAuthStore()
  const darfPflegen = erfuelltRecht('projects.position.manage', user?.permissions)

  const [dateien, setDateien] = useState<ProjectFileRow[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const eingabe = useRef<HTMLInputElement>(null)
  /** Art der nächsten hochgeladenen Dateien — leer: der Server leitet sie ab. */
  const [art, setArt] = useState('')
  /**
   * Die Datei, deren Löschen gerade bestätigt wird. Bewusst **kein**
   * `window.confirm`: Hat der Browser Dialoge der Seite unterdrückt („keine
   * weiteren Dialoge") oder läuft die Seite in einer eingebetteten Ansicht,
   * liefert `confirm` still `false` — der Papierkorb tat dann schlicht nichts,
   * ohne Anfrage und ohne Meldung.
   */
  const [loeschFrage, setLoeschFrage] = useState<string | null>(null)
  const [loescht, setLoescht] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      setDateien(await fetchFiles(projectId, entityType === 'PROJECT' ? {} : { entityType, entityId }))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }, [projectId, entityType, entityId])

  useEffect(() => {
    void laden()
  }, [laden])

  async function aendere(id: string, daten: Record<string, unknown>) {
    try {
      setFehler(null)
      await updateFile(id, daten)
      await laden()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  async function loesche(id: string) {
    setLoescht(id)
    setFehler(null)
    try {
      await deleteFile(id)
      setLoeschFrage(null)
      await laden()
      await onChanged?.()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Datei ließ sich nicht löschen.'))
    } finally {
      setLoescht(null)
    }
  }

  async function hochladen(liste: FileList | null) {
    if (!liste?.length) return
    setLaeuft(true)
    setFehler(null)
    try {
      for (const datei of Array.from(liste)) {
        await uploadFile(projectId, datei, { entityType, entityId, ...(art ? { kind: art } : {}) })
      }
      await laden()
      await onChanged?.()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
      if (eingabe.current) eingabe.current.value = ''
    }
  }

  return (
    <div className={formCard}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">Dokumente</h3>
        {darfPflegen && (
          <span className="flex items-center gap-2">
          <select className="rounded border border-border bg-background px-1 py-1 text-xs" value={art} onChange={(e) => setArt(e.target.value)} aria-label="Art der hochgeladenen Dateien">
            <option value="">Art automatisch</option>
            {Object.entries(FILE_KIND_LABELS).map(([k, l]) => (
              <option key={k} value={k}>
                {l}
              </option>
            ))}
          </select>
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs">
            <Upload className="h-3.5 w-3.5" />
            {laeuft ? 'lädt …' : 'Hochladen'}
            <input
              ref={eingabe}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => void hochladen(e.target.files)}
            />
          </label>
          </span>
        )}
      </div>

      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}

      <table className="mt-3 w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-2 py-1 text-left">Name</th>
            <th className="px-2 py-1 text-left">Art</th>
            <th className="px-2 py-1 text-left">gehört zu</th>
            <th className="px-2 py-1 text-left">hochgeladen</th>
            <th className="px-2 py-1 text-right">Größe</th>
            <th className="px-2 py-1 text-center">PWA</th>
            <th className="px-2 py-1 text-right" />
          </tr>
        </thead>
        <tbody>
          {dateien.map((d) => (
            <tr key={d.id} className="border-t border-border">
              <td className="px-2 py-1">
                {/* Kein Link: das Token steht nicht im Cookie, ein <a href> bekäme 401 (Phase F). */}
                <button
                  type="button"
                  onClick={() =>
                    oeffneProjektdatei(d.id, d.file.originalName).catch((e) => setFehler(apiErrorText(e, 'Die Datei lässt sich nicht öffnen.')))
                  }
                  className="text-left font-medium text-primary hover:underline"
                >
                  {d.file.originalName}
                </button>
                {/* Notiz und Art direkt in der Zeile (Auftraggeber 18.9.2026) — gespeichert beim Verlassen. */}
                {darfPflegen ? (
                  <input
                    key={`${d.id}-${d.note ?? ''}`}
                    className="mt-0.5 block w-full rounded border border-transparent bg-transparent px-1 text-xs text-muted-foreground hover:border-border focus:border-ring focus:bg-background focus:outline-none"
                    placeholder="Notiz …"
                    defaultValue={d.note ?? ''}
                    onBlur={(e) => {
                      if (e.target.value === (d.note ?? '')) return
                      void aendere(d.id, { note: e.target.value.trim() || null })
                    }}
                  />
                ) : (
                  d.note && <span className="block text-xs text-muted-foreground">{d.note}</span>
                )}
              </td>
              <td className="px-2 py-1">
                {darfPflegen ? (
                  <select
                    className="rounded border border-border bg-background px-1 py-0.5 text-sm"
                    value={d.kind}
                    onChange={(e) => void aendere(d.id, { kind: e.target.value })}
                  >
                    {Object.entries(FILE_KIND_LABELS).map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </select>
                ) : (
                  (FILE_KIND_LABELS[d.kind] ?? d.kind)
                )}
              </td>
              <td className="px-2 py-1 text-xs text-muted-foreground">
                {ENTITY_TYPE_LABELS[d.entityType] ?? d.entityType}
              </td>
              <td className="px-2 py-1 text-xs text-muted-foreground">
                {alsZeitpunkt(d.uploadedAt)}
                {d.uploadedByName && ` · ${d.uploadedByName}`}
              </td>
              <td className="px-2 py-1 text-right text-xs">{alsGroesse(d.file.sizeBytes)}</td>
              <td className="px-2 py-1 text-center">
                <button
                  type="button"
                  disabled={!darfPflegen}
                  title={d.visibleInPwa ? `In der ${terms.folder} sichtbar` : `Nicht in der ${terms.folder}`}
                  onClick={() =>
                    void updateFile(d.id, { visibleInPwa: !d.visibleInPwa })
                      .then(laden)
                      .catch((e) => setFehler(apiErrorText(e, 'Das hat nicht geklappt.')))
                  }
                >
                  {d.visibleInPwa ? (
                    <Eye className="h-4 w-4 text-primary" />
                  ) : (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  )}
                </button>
              </td>
              <td className="px-2 py-1 text-right">
                {darfPflegen && !d.locked && loeschFrage !== d.id && (
                  <button
                    type="button"
                    className="text-destructive"
                    title="Löschen"
                    aria-label={`${d.file.originalName} löschen`}
                    onClick={() => setLoeschFrage(d.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
                {darfPflegen && !d.locked && loeschFrage === d.id && (
                  <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs">
                    <button
                      type="button"
                      className="rounded bg-destructive px-2 py-0.5 font-medium text-destructive-foreground disabled:opacity-50"
                      disabled={loescht === d.id}
                      onClick={() => void loesche(d.id)}
                    >
                      {loescht === d.id ? 'löscht …' : 'Löschen'}
                    </button>
                    <button
                      type="button"
                      className="text-muted-foreground hover:underline"
                      disabled={loescht === d.id}
                      onClick={() => setLoeschFrage(null)}
                    >
                      Abbrechen
                    </button>
                  </span>
                )}
              </td>
            </tr>
          ))}
          {dateien.length === 0 && (
            <tr>
              <td colSpan={7} className="px-2 py-4 text-sm text-muted-foreground">
                Noch keine Dateien.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
